import type { Body, Meta, Uppy, UppyFile } from '@uppy/core';

type PresignedPutCredentials =
	| { readonly putUrl: string | null; readonly putContentType?: string | null }
	| null
	| undefined;

type FileToUpload = { id: string; name: string; size: number };

type PresignedPutUploadOptions<M extends Meta, B extends Body> = {
	shouldUseMultipart: false;
	generateObjectKey: (file: UppyFile<M, B>) => string;
	signRequest: (request: { key: string }) => Promise<{ url: string; headers?: Record<string, string> }>;
};

/**
 * AwsS3 plugin options that upload each file with a single presigned PUT issued by the middle-end.
 *
 * AwsS3 only passes the object key to `signRequest`, and the middle-end needs the file's name and
 * exact size to issue a URL. So the key given to AwsS3 is the Uppy file id, which `signRequest`
 * uses to look the file up. The real S3 key is chosen by the middle-end and is already in the
 * returned URL.
 */
export function presignedPutUploadOptions<M extends Meta, B extends Body>(
	uppy: Uppy<M, B>,
	getCredentials: (file: FileToUpload) => Promise<PresignedPutCredentials>,
): PresignedPutUploadOptions<M, B> {
	return {
		shouldUseMultipart: false,
		generateObjectKey: (file) => file.id,
		signRequest: async ({ key: fileId }) => {
			// read the file at upload time rather than when it was added: preprocessors such as
			// @uppy/compressor replace its data, and the URL is signed for the exact size sent
			const file = uppy.getFile(fileId) as UppyFile<M, B> | undefined;
			const size = file?.data?.size ?? file?.size;
			if (!file || size == null) {
				throw new Error('Could not find the file to upload');
			}

			const credentials = await getCredentials({ id: file.id, name: file.name, size });
			if (!credentials?.putUrl) {
				throw new Error('Could not get upload credentials. Please try again.');
			}
			// the URL is signed for this exact Content-Type, so send it instead of the browser-reported file type
			return credentials.putContentType
				? { url: credentials.putUrl, headers: { 'Content-Type': credentials.putContentType } }
				: { url: credentials.putUrl };
		},
	};
}
